import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import { Funnel } from "@b2bportal/core-types";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import type {
  HsbcPromotedDestinations,
  PromotedDestinationCity,
} from "@hsbc/portal";
import { HsbcPortalComponent, useHsbcPortalStyles } from "@hsbc/portal";
import {
  Button,
  ButtonTypeEnum,
  Dialog,
  DialogContent,
  DialogTrigger,
  Icon,
} from "@tenant/ui";
import { PremierDestinationCountryTrigger } from "../PremierDestinationBanner";
import { LearnMoreDialog } from "./LearnMoreDialog";
import defaultStyles from "./styles.module.scss";

type PremierDestinationQuickPicksProps = {
  funnel: Funnel;
  activePremierDestinations: HsbcPromotedDestinations[];
  onSelect: (city: PromotedDestinationCity) => void;
};

export const PremierDestinationQuickPicks: React.FC<
  PremierDestinationQuickPicksProps
> = ({ funnel, onSelect, activePremierDestinations }) => {
  const { t } = useI18nContext();

  const styles = useHsbcPortalStyles(
    HsbcPortalComponent.PremierDestinationQuickPicks,
    defaultStyles
  );
  const [block, cn] = useModuleBEM(
    styles,
    HsbcPortalComponent.PremierDestinationQuickPicks
  );

  return (
    <section className={block}>
      <header>
        <h3>{t("premierDestinations.title")}</h3>
        <div className={cn("tag", ["title"])}>
          {t("premierDestinations.earnBonusMiles")}
        </div>
      </header>
      <div className={cn("description")}>
        {funnel === Funnel.Lodging && (
          <Trans
            i18nKey="premierDestinations.hotelDescription"
            components={[<span className={cn("bold")} />, <LearnMoreDialog />]}
          />
        )}
        {funnel === Funnel.Air && (
          <Trans
            i18nKey="premierDestinations.flightsDescription"
            components={[<span className={cn("bold")} />, <LearnMoreDialog />]}
          />
        )}
        {funnel === Funnel.Ground && (
          <Trans
            i18nKey="premierDestinations.carsDescription"
            components={[<span className={cn("bold")} />, <LearnMoreDialog />]}
          />
        )}
      </div>
      <div
        className={cn("country-list", {
          ["2-columns"]: activePremierDestinations.length > 4,
        })}
      >
        {activePremierDestinations.map((country) => (
          <Dialog key={country.title}>
            <DialogTrigger>
              <PremierDestinationCountryTrigger country={country}>
                {country.title}
              </PremierDestinationCountryTrigger>
            </DialogTrigger>
            <DialogContent
              title={t("premierDestinations.citySelectionPopup.title", {
                country: country.title,
              })}
              className={cn("dialog-content")}
            >
              <p className={cn("fine-print")}>
                {t("premierDestinations.citySelectionPopup.finePrint")}
              </p>
              <h3>
                {t("premierDestinations.citySelectionPopup.popularCities")}
              </h3>
              <div className={cn("result-container")}>
                {country.cities.map((city) => (
                  <Button
                    buttonType={ButtonTypeEnum.Wrap}
                    key={city.label}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                    }}
                    onClick={() => onSelect(city)}
                  >
                    <div className={cn("result-item")}>
                      <div className={cn("result-icon")}>
                        <Icon iconName={IconNameEnum.location} />
                      </div>
                      <div className={cn("label")}>{city.label}</div>
                      <div className={cn("tag")}>
                        {t("premierDestinations.tag")}
                      </div>
                    </div>
                  </Button>
                ))}
              </div>
            </DialogContent>
          </Dialog>
        ))}
      </div>
    </section>
  );
};
